import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { mfaApiCall } from '../services/api';
import toast from 'react-hot-toast';
import OtpInput from 'react-otp-input';
import { QRCodeSVG } from 'qrcode.react';

interface MFASetupModalProps {
  onSuccess: (result: any) => void;
  onCancel: () => void;
}

const MFASetupModal: React.FC<MFASetupModalProps> = ({
  onSuccess,
  onCancel
}) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [qrCodeData, setQrCodeData] = useState('');
  const [secretCode, setSecretCode] = useState('');

  useEffect(() => {
    initializeMFASetup();
  }, []);

  const initializeMFASetup = async () => {
    try {
      setLoading(true);
      const response = await mfaApiCall('/auth/mfa/setup-init', {
        method: 'POST'
      });

      setSecretCode(response.secretCode);
      setQrCodeData(response.qrCodeUrl);
    } catch (error: any) {
      console.error('MFA setup initialization error:', error);
      toast.error('Failed to initialize MFA setup');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (code.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    try {
      setLoading(true);
      
      // Verify with backend - this calls Cognito's verifySoftwareToken and setUserMFAPreference
      await mfaApiCall('/auth/mfa/setup-verify', {
        method: 'POST',
        body: JSON.stringify({ code })
      });
      
      toast.success('MFA setup completed successfully!');
      
      // Pass a success indicator to parent - user is already logged in
      onSuccess({ mfaSetupComplete: true });
    } catch (error: any) {
      console.error('MFA setup verification error:', error?.message);
      toast.error(error.message || 'Invalid verification code. Please try again.');
      setCode(''); // Clear the code on error
    } finally {
      setLoading(false);
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="text-center mb-6">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
            <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">
            Set Up Two-Factor Authentication
          </h3>
          <p className="text-sm text-gray-600">
            Scan the QR code with your authenticator app
          </p>
        </div>

        {loading && !qrCodeData ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <div className="bg-gray-50 rounded-lg p-6 text-center">
                {qrCodeData && (
                  <div className="inline-block p-4 bg-white rounded-lg">
                    <QRCodeSVG 
                      value={qrCodeData} 
                      size={200}
                      level="M"
                      includeMargin={true}
                    />
                  </div>
                )}
                
                <div className="mt-4">
                  <p className="text-xs text-gray-600 mb-2">
                    Can't scan? Enter this code manually:
                  </p>
                  <div className="bg-white border border-gray-300 rounded px-3 py-2 font-mono text-sm break-all">
                    {secretCode}
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <h4 className="text-sm font-semibold text-blue-900 mb-2">
                  Setup Instructions:
                </h4>
                <ol className="text-xs text-blue-800 space-y-1 list-decimal list-inside">
                  <li>Download an authenticator app (Google Authenticator, Authy, etc.)</li>
                  <li>Scan the QR code above with your app</li>
                  <li>Enter the 6-digit code from your app below</li>
                </ol>
              </div>
            </div>

            <form onSubmit={handleVerify} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 text-center">
                  Enter Verification Code
                </label>
                <div className="flex justify-center">
                  <OtpInput
                    value={code}
                    onChange={setCode}
                    numInputs={6}
                    renderSeparator={<span className="mx-1"></span>}
                    renderInput={(props) => (
                      <input
                        {...props}
                        className="w-12 h-12 text-center text-2xl font-bold border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                      />
                    )}
                    shouldAutoFocus
                    inputType="tel"
                  />
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={onCancel}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || code.length !== 6}
                  className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Verifying...' : 'Complete Setup'}
                </button>
              </div>
            </form>

            <div className="mt-6 text-center">
              <p className="text-xs text-gray-500">
                Keep your authenticator app safe. You'll need it to log in.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  , document.body);
};

export default MFASetupModal;
